export type ApiPage<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};
export type ApiEnvelope<T> =
  | T[]
  | Partial<Omit<ApiPage<T>, "items">> & { items: T[]; next_cursor?: string | null }
  | T;

export type SourceTag = {
  source?: string | null;
  provider?: string | null;
  provider_name?: string | null;
  status?: string | null;
  authorization_status?: string | null;
  data_classification?: string | null;
  is_synthetic?: boolean | null;
  synthetic?: boolean | null;
  is_delayed?: boolean | null;
  delayed?: boolean | null;
  authorization_id?: string | null;
  dataset_version?: string | null;
};
export type DataReadiness = SourceTag & {
  datasets_total?: number;
  instruments_total?: number;
  ready?: boolean;
  latest_dataset_version?: string | null;
  latest_source_ts?: string | null;
};
export type Dashboard = SourceTag & {
  data_readiness?: DataReadiness;
  strategies?: { total?: number; latest_created_at?: string | null };
  research?: {
    total?: number;
    latest_status?: string | null;
    latest_started_at?: string | null;
    backtests_total?: number;
  };
  simulation?: {
    total?: number;
    paper_automation_runs?: number;
    legacy_replays?: number;
    latest_state?: string | null;
    latest_created_at?: string | null;
  };
  [key: string]: unknown;
};
export type CatalogSummary = SourceTag & {
  research_only?: boolean;
  datasets_total?: number;
  instruments_total?: number;
  latest_dataset?: Dataset | null;
  quality_issue_counts?: Record<string, number>;
  [key: string]: unknown;
};
export type Dataset = SourceTag & {
  id?: string;
  dataset_id?: string;
  dataset_version?: string;
  name?: string;
  description?: string | null;
  frequency?: string | null;
  bar_interval?: string | null;
  adjustment?: string | null;
  provider_metadata?: Record<string, unknown>;
  bar_count?: number;
  instrument_count?: number;
  coverage_start?: string | null;
  coverage_end?: string | null;
  earliest_source_ts?: string | null;
  latest_source_ts?: string | null;
  created_at?: string | null;
  market_scope?: string | null;
  membership_mode?: string | null;
  supported_use_cases?: string[];
  blocked_reasons?: string[];
  [key: string]: unknown;
};
export type Instrument = SourceTag & {
  id?: string;
  instrument_id?: string;
  symbol?: string;
  code?: string;
  name?: string;
  market?: string | null;
  exchange?: string | null;
  status?: string | null;
  trading_status?: string | null;
  board?: string | null;
  list_date?: string;
  delist_date?: string | null;
  is_st?: boolean;
  is_risk_warning?: boolean;
  [key: string]: unknown;
};
export type Bar = SourceTag & {
  id?: number;
  instrument_id?: string;
  timestamp?: string;
  ts?: string;
  time?: string;
  trade_date?: string;
  open?: number | string;
  high?: number | string;
  low?: number | string;
  close?: number | string;
  volume?: number | string;
  amount?: number | string;
  turnover_amount?: number | string | null;
  ingested_at?: string;
  raw_batch_id?: string;
  [key: string]: unknown;
};
export type KlineInterval = "1d" | "5m" | "1m";
export type KlineSeries = SourceTag & {
  instrument_id: string;
  bar_interval: KlineInterval;
  data_domain: "full_market_daily_foundation" | "targeted_instrument_minutes";
  dataset_version: string | null;
  analysis_hint: string;
  signal_frequency: string;
  coverage_start: string | null;
  coverage_end: string | null;
  items: Bar[];
  limit: number;
  empty_reason?: string | null;
};
export type AvailabilitySection = {
  status: "not_ingested" | "available" | string;
  items: Array<Record<string, unknown>>;
  reason?: string;
};
export type InstrumentContext = {
  instrument: Instrument;
  daily_references: Array<Record<string, unknown>>;
  related_research: Array<Record<string, unknown>>;
  paper_positions: Array<Record<string, unknown>>;
  financials: AvailabilitySection;
  capital_flows: AvailabilitySection;
  market_events: AvailabilitySection;
  source_boundary: string;
};

/** Phase 4 adapter types mirror the V1 request/response fields while using V2 paths. */
export type Strategy = {
  id: number;
  name: string;
  description?: string;
  created_at?: string;
  frozen?: boolean;
  [key: string]: unknown;
};
export type StrategyVersion = {
  id: number;
  strategy_id?: number;
  strategy_name?: string | null;
  version: string;
  name?: string;
  description?: string;
  parameters?: Record<string, unknown>;
  exit_time?: string;
  signal_time?: string;
  frozen?: boolean;
  dataset_version?: string;
  [key: string]: unknown;
};
export type ResearchRun = SourceTag & {
  id: number;
  strategy_version_id: number;
  dataset_version: string;
  signal_ts: string;
  status?: string;
  progress?: number;
  progress_percent?: number;
  snapshot?: CandidateSnapshot | null;
  audit_metadata?: Record<string, unknown>;
  data_provenance?: Record<string, unknown>;
  execution_model_version?: string;
  code_config_version?: string;
  no_future_data_audit?: Record<string, unknown> | boolean;
  created_at?: string;
  started_at?: string;
  completed_at?: string | null;
  [key: string]: unknown;
};
export type ResearchCandidate = SourceTag & {
  id: number;
  instrument_id: string;
  rank?: number | null;
  eligible: boolean;
  selected?: boolean;
  status?: string;
  reject_reason?: string | null;
  rejection_reasons?: Array<Record<string, unknown>>;
  missing_data_reasons?: Array<Record<string, unknown>>;
  signal_score?: number | string | null;
  reference_price?: number | string;
  available_volume?: number | string;
  target_weight?: number | string;
  max_input_ts?: string | null;
  features?: Record<string, unknown>;
  [key: string]: unknown;
};
export type CandidateSnapshot = {
  id?: number;
  research_run_id?: number;
  candidates: ResearchCandidate[];
  snapshot_hash?: string;
  created_at?: string;
  total?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  universe_count?: number;
  eligible_count?: number;
  rejected_count?: number;
  [key: string]: unknown;
};
export type Backtest = SourceTag & {
  id: number;
  instrument_id?: string;
  strategy_version_id?: number;
  strategy_name?: string | null;
  strategy_version?: string | null;
  dataset_version?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  execution_model_version?: string;
  code_config_version?: string;
  signal_ts?: string;
  summary?: Record<string, unknown>;
  created_at?: string;
  completed_at?: string | null;
  [key: string]: unknown;
};
export type PortfolioBacktestDay = {
  id?: number;
  trade_date: string;
  status: string;
  audit_passed: boolean;
  universe_count?: number;
  eligible_count?: number;
  selected_count?: number;
  attempted_count?: number;
  filled_count?: number;
  rejected_count?: number;
  unclosed_count?: number;
  day_summary?: {
    selected_instruments?: string[];
    ending_cash?: string;
    net_pnl?: string;
    fees?: string;
    failure_reason?: string;
    message?: string;
  };
  [key: string]: unknown;
};
export type PortfolioBacktest = SourceTag & {
  id: number;
  portfolio_type: "cross_date_top_k_portfolio" | "multi_instrument_portfolio";
  product_label: string;
  strategy_version_id: number;
  strategy_name?: string | null;
  strategy_version?: string | null;
  dataset_version: string;
  start_date?: string | null;
  end_date?: string | null;
  top_k: number;
  initial_cash: string;
  execution_model_version: string;
  participation_rate: string;
  instrument_ids: string[];
  status: string;
  summary?: BacktestMetrics | null;
  data_coverage?: Record<string, unknown>;
  data_provenance?: Record<string, unknown>;
  limitations?: string[];
  days?: PortfolioBacktestDay[];
  audit_metadata?: Record<string, unknown>;
  report_hash?: string | null;
  failure_reason?: string | null;
  started_at?: string;
  completed_at?: string | null;
  research_only?: boolean;
  diagnostic_only?: false;
  profit_claim?: false;
  [key: string]: unknown;
};
export type BacktestMetric = {
  key?: string;
  name?: string;
  label?: string;
  value?: number | string | null;
  [key: string]: unknown;
};
export type BacktestTrade = {
  id?: number;
  instrument_id?: string;
  side?: string;
  requested_quantity?: number | string;
  quantity?: number | string;
  filled_quantity?: number | string;
  entry_price?: number | string | null;
  exit_price?: number | string | null;
  price?: number | string;
  execution_price?: number | string;
  entry_ts?: string | null;
  exit_ts?: string | null;
  ts?: string;
  net_pnl?: number | string | null;
  status?: string;
  reason?: string | null;
  [key: string]: unknown;
};
export type EquityCurvePoint = {
  ts?: string;
  timestamp?: string;
  equity?: number | string;
  value?: number | string;
  realized_pnl?: number | string;
};
export type BacktestMetrics = {
  status?: string;
  decision?: string;
  scope?: string;
  trade_count?: number;
  win_rate?: number | string;
  net_total_return?: number | string;
  net_return?: number | string;
  net_pnl?: number | string;
  max_drawdown?: number | string;
  max_drawdown_return?: number | string;
  initial_cash?: number | string;
  ending_cash?: number | string;
  fees?: number | string;
  filled_count?: number;
  rejected_count?: number;
  audit_failed_days?: number;
  processing_failed_days?: number;
  total_fees?: number | string;
  slippage_cost?: number | string;
  rejection_rate?: number | string;
  partial_fill_rate?: number | string;
  unclosed_position_count?: number;
  metrics?: BacktestMetric[];
  equity_curve?: EquityCurvePoint[];
  performance?: ({ equity_curve?: EquityCurvePoint[] } & Record<string, unknown>) | null;
  limitations?: string[];
  [key: string]: unknown;
};
export type BacktestAudit = {
  run?: unknown[];
  signals?: unknown[];
  run_total?: number;
  signals_total?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  [key: string]: unknown;
};
export type BacktestOptions = {
  execution_models: Array<{ value: string; label: string; description?: string }>;
  participation_rate_options: string[];
  top_k_options: number[];
  backtest_types: Array<{ value: string; label: string; available: boolean; blocked_reason?: string; description?: string }>;
  universe_modes: string[];
  benchmark_options: Array<{ value: string; label: string }>;
};
export type StrategyTemplate = {
  key: string;
  label: string;
  description: string;
  frequency: string;
  category?: string;
  risk_level?: string;
  holding_period?: string;
  rebalance_frequency?: string;
  universe?: string;
  source_type?: "built_in" | string;
  editable?: boolean;
  available: boolean;
  blocked_reason?: string;
  operational_status?: string;
  evidence_status?: string;
  result_scope?: string;
  data_adjustment?: string;
  strategy_version?: string;
  signal_time?: string;
  entry_time?: string;
  entry_rule?: string;
  exit_rule?: string;
  exit_time?: string;
  rules?: Array<{ title: string; description: string }>;
  parameters?: Array<{ key: string; label: string; default: string; description?: string }>;
  engine_parameters?: Record<string, unknown>;
  data_requirements?: string[];
  risk_notes?: string[];
  example_code?: string;
  date_policy?: { mode: string; lookback_sessions?: number; manual_date_optional?: boolean };
};
export type DiagnosticBacktest = {
  run: Record<string, unknown>;
  results: Array<Record<string, unknown>>;
  metrics: Record<string, unknown>;
  results_total: number;
  audit_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  diagnostic_only: true;
  research_only: true;
};
export type PaperStrategyRun = {
  id: number;
  state?: string;
  status?: string;
  dataset_version?: string;
  trade_date?: string;
  initial_cash?: string | number;
  candidate_snapshot?: Record<string, unknown> | null;
  orders?: Array<Record<string, unknown>>;
  equity_curve?: EquityPoint[];
  risk_events?: Array<Record<string, unknown>>;
  paper_only?: boolean;
  [key: string]: unknown;
};

/** Phase 5 adapters. These intentionally accept additive backend fields so the UI
 * remains useful while the V2 control-plane schemas are being finalized. */
export type PaperAccount = SourceTag & {
  id: string | number;
  account_id?: string | number;
  name?: string;
  security?: string;
  mode?: "historical" | "polling" | string;
  initial_cash?: number | string;
  cash?: number | string;
  equity?: number | string;
  total_equity?: number | string;
  paper_only?: boolean;
  data_status?: unknown;
  created_at?: string;
  [key: string]: unknown;
};
export type PaperPosition = {
  id?: string | number;
  instrument_id?: string;
  symbol?: string;
  security?: string;
  name?: string;
  quantity?: number | string;
  available?: number | string;
  available_quantity?: number | string;
  average_cost?: number | string;
  avg_cost?: number | string;
  market_price?: number | string;
  market_value?: number | string;
  pnl?: number | string;
  [key: string]: unknown;
};
export type PaperOrder = {
  id?: string | number;
  account_id?: string | number;
  instrument_id?: string;
  symbol?: string;
  side?: "buy" | "sell" | string;
  quantity?: number | string;
  filled_quantity?: number | string;
  price?: number | string;
  status?: string;
  reason?: string | null;
  created_at?: string;
  [key: string]: unknown;
};
export type PaperFill = {
  id?: string | number;
  order_id?: string | number;
  instrument_id?: string;
  symbol?: string;
  side?: string;
  quantity?: number | string;
  price?: number | string;
  fee?: number | string;
  filled_at?: string;
  [key: string]: unknown;
};
export type EquityPoint = {
  timestamp?: string;
  ts?: string;
  date?: string;
  equity?: number | string;
  value?: number | string;
  cash?: number | string;
  [key: string]: unknown;
};
export type RiskEvent = {
  id: string | number;
  account_id?: string | number;
  severity?: "info" | "warning" | "critical" | string;
  event_type?: string;
  type?: string;
  message?: string;
  detail?: string;
  resolved?: boolean;
  read_only?: boolean;
  paper_only?: boolean;
  created_at?: string;
  [key: string]: unknown;
};
export type ProviderCapability = {
  status?: string;
  available?: boolean;
  source?: string;
  message?: string;
  [key: string]: unknown;
};
export type Settings = {
  version: number;
  paper_only?: boolean;
  broker_credentials_supported?: boolean;
  providers?: Record<string, ProviderCapability>;
  fees?: {
    commission_rate?: number;
    minimum_commission?: number;
    sell_stamp_duty_rate?: number;
    transfer_fee_rate?: number;
    slippage_bps?: number;
  };
  risk?: { paper_data_stale_after_seconds?: number };
  audit?: { action?: string; sensitive_values?: string };
  [key: string]: unknown;
};
export type Job = {
  id: string;
  job_type: string;
  status:
    | "queued"
    | "running"
    | "retrying"
    | "succeeded"
    | "failed"
    | "cancelled"
    | string;
  payload?: Record<string, unknown>;
  checkpoint?: Record<string, unknown>;
  completed_units?: number;
  total_units?: number | null;
  failed_members?: Array<Record<string, unknown>>;
  attempt_count?: number;
  max_attempts?: number;
  cancel_requested?: boolean;
  heartbeat_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  failure_detail?: string | null;
  created_at?: string;
  updated_at?: string;
};
export type Session = {
  authenticated?: boolean;
  user?: {
    id?: string | number;
    name?: string;
    email?: string;
    [key: string]: unknown;
  } | null;
  csrf_token?: string;
  csrfToken?: string;
  expires_at?: string | null;
  [key: string]: unknown;
};
export type Provider = {
  name?: string;
  provider?: string;
  availability?: string;
  capability?: string;
  authorization_required?: boolean;
  adapter_status?: string;
  message?: string;
  [key: string]: unknown;
};
export type DatasetDetail = Dataset & {
  quality?: Record<string, number>;
  coverage?: Record<string, unknown>;
  lineage?: Record<string, unknown>;
};
export type QualityEvent = {
  id: string | number;
  event_type?: string;
  severity?: string;
  message?: string;
  detail?: string;
  dataset_version?: string;
  instrument_id?: string;
  created_at?: string;
  [key: string]: unknown;
};
export type ImportJob = {
  job_id?: string;
  id?: string;
  status?: string;
  job_type?: string;
  result?: unknown;
  failure_detail?: string;
  [key: string]: unknown;
};
export type ResearchFactor = {
  name?: string;
  factor?: string;
  instrument_id?: string;
  value?: number | string | null;
  date?: string;
  dataset_version?: string;
  strategy_version_id?: string | number;
  [key: string]: unknown;
};
export type ResearchEvent = QualityEvent & {
  research_run_id?: string | number;
  category?: string;
  source?: string;
};
export type SystemMetric = {
  name?: string;
  value?: number | string;
  unit?: string;
  status?: string;
  observed_at?: string;
  [key: string]: unknown;
};
export type SystemMetrics = {
  started_at?: string;
  requests?: number;
  errors?: number;
  latency_histogram?: Record<string, number>;
  jobs?: Record<string, number>;
  readiness?: Record<string, unknown>;
  [key: string]: unknown;
};

export type IndexPoolVersion = {
  id: string;
  pool_code: "000300" | "000905";
  version_number: number;
  as_of_date: string;
  member_count: number;
  member_hash: string;
  source: string;
  fetched_at?: string | null;
  immutable: true;
};
export type IndexPool = {
  code: "000300" | "000905";
  name: "沪深300" | "中证500" | string;
  target_count: 300 | 500 | number;
  fixed: true;
  latest_version?: IndexPoolVersion | null;
  schedule?: {
    enabled: boolean;
    cron: string;
    timezone: string;
    last_run_at?: string | null;
    next_run_at?: string | null;
    last_outcome?: string | null;
  } | null;
};
export type IndexPoolMember = {
  id: string;
  index_pool_version_id: string;
  instrument_id: string;
  name: string;
  exchange: string;
  created_at?: string | null;
};

export type StockPoolAutomation = {
  enabled: boolean;
  cron?: string;
  timezone?: string;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_status?: string | null;
  last_error?: string | null;
};
export type StockPool = {
  id: string | number;
  name: string;
  description?: string | null;
  strategy_version_id: string | number;
  strategy_name?: string | null;
  strategy_version?: string | null;
  dataset_version: string;
  index_pool_code?: "000300" | "000905" | null;
  top_k: number;
  recent_signal_days?: number;
  status?: string;
  auto_refresh_enabled?: boolean;
  refresh_schedule?: StockPoolAutomation;
  automation?: StockPoolAutomation;
  current_version_id?: string | number | null;
  latest_version?: StockPoolVersion | null;
  latest_job_id?: string | null;
  last_run_at?: string | null;
  next_run_at?: string | null;
  failure_detail?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};
export type StockPoolVersion = {
  id: string | number;
  pool_id?: string | number;
  version?: string | number;
  version_no?: number;
  version_number?: number;
  research_run_id?: string | number | null;
  job_id?: string | null;
  status?: string;
  signal_ts?: string | null;
  effective_at?: string | null;
  dataset_version?: string;
  strategy_version_id?: string | number;
  member_count?: number;
  selected_count?: number;
  eligible_count?: number;
  rejected_count?: number;
  added_count?: number;
  removed_count?: number;
  retained_count?: number;
  failure_detail?: string | null;
  created_at?: string;
  completed_at?: string | null;
  [key: string]: unknown;
};
export type StockPoolMember = {
  id?: string | number;
  pool_version_id?: string | number;
  instrument_id: string;
  symbol?: string;
  name?: string | null;
  rank?: number | null;
  eligible: boolean;
  selected?: boolean;
  status?: string;
  signal_score?: number | string | null;
  target_weight?: number | string | null;
  decision_reason?: string | null;
  reject_reason?: string | null;
  rejection_reasons?: Array<string | Record<string, unknown>>;
  missing_data_reasons?: Array<string | Record<string, unknown>>;
  features?: Record<string, unknown>;
  max_input_ts?: string | null;
  change?: "added" | "removed" | "retained" | string | null;
  [key: string]: unknown;
};
export type StockPoolDiff = {
  from_version_id?: string | number | null;
  to_version_id?: string | number;
  added: StockPoolMember[];
  removed: StockPoolMember[];
  retained?: StockPoolMember[];
  added_count?: number;
  removed_count?: number;
  retained_count?: number;
  counts?: { added?: number; removed?: number; retained?: number };
};
export type StockPoolRunResult = {
  id?: string | number;
  pool_id?: string | number;
  pool_version_id?: string | number | null;
  version_id?: string | number | null;
  job_id?: string | null;
  status?: string;
  failure_reason?: string | null;
  [key: string]: unknown;
};
export type StockPoolRun = StockPoolRunResult & {
  id: string | number;
  stock_pool_id: string | number;
  trigger_type?: "manual" | "scheduled" | string;
  signal_ts?: string;
  strategy_version_id?: string | number;
  dataset_version?: string;
  top_k?: number;
  research_run_id?: string | number | null;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string;
};
export type StockPoolConsumption = {
  id: string;
  stock_pool_version_id: string;
  consumer_type: "backtest" | "paper" | string;
  consumer_identity_space?: string;
  consumer_id: string;
  snapshot_hash?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
};
export type StockPoolBacktestResult = {
  id: string | number;
  status?: string;
  stock_pool_id: string;
  stock_pool_version_id: string;
  stock_pool_snapshot_hash?: string;
  selected_instrument_ids?: string[];
  summary?: Record<string, unknown>;
  research_only?: boolean;
  [key: string]: unknown;
};
export type StockPoolPaperResult = {
  id: string | number;
  state?: string;
  status?: string;
  stock_pool_id: string;
  stock_pool_version_id: string;
  stock_pool_snapshot_hash?: string;
  selected_instrument_ids?: string[];
  paper_only: true;
  broker_order_submission: false;
  [key: string]: unknown;
};
export type PaperExecutionStrategyVersion = {
  id: number;
  execution_strategy_id?: number;
  execution_strategy_name?: string;
  version: string;
  mode?: string;
  frozen?: boolean;
  max_candidates?: number;
  allow_partial_fill?: boolean;
  linked_research_strategy_version?: {
    id?: number;
    strategy_name?: string;
    version?: string;
    read_only?: boolean;
  };
  portfolio_policy?: Record<string, unknown>;
  risk_policy?: Record<string, unknown>;
  paper_only?: boolean;
  [key: string]: unknown;
};

export function unwrapPage<T>(payload: ApiEnvelope<T>, limit: number, offset: number): ApiPage<T> {
  const items = unwrapItems(payload);
  if (payload && typeof payload === "object" && "items" in payload) {
    const page = payload as Partial<ApiPage<T>>;
    return {
      items,
      total: Number(page.total ?? items.length),
      limit: Number(page.limit ?? limit),
      offset: Number(page.offset ?? offset),
      has_more: Boolean(page.has_more ?? items.length === limit),
    };
  }
  return { items, total: items.length, limit, offset, has_more: false };
}

export function unwrapItems<T>(payload: ApiEnvelope<T>): T[] {
  if (Array.isArray(payload)) return payload;
  if (
    payload &&
    typeof payload === "object" &&
    "items" in payload &&
    Array.isArray(payload.items)
  )
    return payload.items;
  return [];
}
export function sourceMeta(value: SourceTag | null | undefined) {
  const classification = value?.data_classification?.toLowerCase() ?? "";
  return {
    source:
      value?.source ?? value?.provider ?? value?.provider_name ?? "来源待标注",
    synthetic: value?.is_synthetic ?? value?.synthetic ?? false,
    delayed:
      value?.is_delayed ?? value?.delayed ?? classification.includes("delay"),
    version: value?.dataset_version ?? null,
  };
}
